import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import clsx from "clsx";

import appInfo from "utility/app-info";
import {
  LEARNER_SCREEN_INDEX_GRAPH_MONTH,
  LEARNER_SCREEN_INDEX_GRAPH_WEEK,
  LEARNER_SCREEN_INDEX_GRAPH_YEAR,
  LEARNER_SCREEN_INDEX_V1,
  LEARNER_SCREEN_INDEX_V2,
} from "utility/graphQl/query";
import {
  LearnerScreenIndexQuery,
  LearnerIndexScore,
} from "apps/user/learner/api/types";

import {
  PageContainer,
  PageHeader,
  PageContent,
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";

import IndexScoreContainer from "./components/index-score-container";
import IndexScoreGraph from "./components/index-score-graph";

import IndexScoreIcon from "Assets/icon/iconIndexScore.svg";
import SelfAssessmentScoreIcon from "Assets/icon/self-assessment-score.svg";
import BiometricsIcon from "Assets/icon/biometrics.svg";

import styles from "./index.module.scss";
import { useAuth } from "providers/auth";
import { useQuery } from "@apollo/client";
import { useTranslation } from "lib/i18n/use-translation";

interface WoneIndexDetailsContentProps {
  score?: LearnerIndexScore;
  fetchMore: any;
  loading: boolean;
}

const WoneIndexDetailsContent = ({
  loading,
  score,
  fetchMore,
}: WoneIndexDetailsContentProps) => {
  const { t } = useTranslation("index");
  const [weekPeriodIndex, setWeekPeriodIndex] = useState(0);
  const [monthPeriodIndex, setMonthPeriodIndex] = useState(0);
  const [yearPeriodIndex, setYearPeriodIndex] = useState(0);
  const [periodLength, setPeriodLength] = useState<"week" | "month" | "year">(
    "week"
  );
  let periodQuery, periodIndex;
  if (periodLength === "week") {
    periodQuery = LEARNER_SCREEN_INDEX_GRAPH_WEEK;
    periodIndex = weekPeriodIndex;
  } else if (periodLength === "month") {
    periodQuery = LEARNER_SCREEN_INDEX_GRAPH_MONTH;
    periodIndex = monthPeriodIndex;
  } else if (periodLength === "year") {
    periodQuery = LEARNER_SCREEN_INDEX_GRAPH_YEAR;
    periodIndex = yearPeriodIndex;
  } else {
    throw new Error(`Unexpected periodLength value: ${periodLength}`);
  }

  const previousPeriodIndex = periodIndex + 1;
  const currentPeriodsLength = score?.graph.periods[periodLength]?.length;
  const shouldFetchMore = previousPeriodIndex >= currentPeriodsLength;

  const handleClickPreviousPeriod = async () => {
    if (shouldFetchMore) {
      await fetchMore({
        query: periodQuery,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
        variables: {
          appInfo,
          first: 1,
          offset: currentPeriodsLength,
        },
      });
    }

    if (periodLength === "week") {
      setWeekPeriodIndex(previousPeriodIndex);
    } else if (periodLength === "month") {
      setMonthPeriodIndex(previousPeriodIndex);
    } else if (periodLength === "year") {
      setYearPeriodIndex(previousPeriodIndex);
    }
  };

  const handleClickNextPeriod = () => {
    if (periodLength === "week") {
      if (weekPeriodIndex > 0) {
        setWeekPeriodIndex((prev) => prev - 1);
      }
    } else if (periodLength === "month") {
      if (monthPeriodIndex > 0) {
        setMonthPeriodIndex((prev) => prev - 1);
      }
    } else if (periodLength === "year") {
      if (yearPeriodIndex > 0) {
        setYearPeriodIndex((prev) => prev - 1);
      }
    }
  };

  const handleChangePeriodLength = (value: "week" | "month" | "year") => {
    setPeriodLength(value);
  };

  if (!score) {
    return <Navigate to="/user/woneindex" />;
  }

  return (
    <div className={styles["wone-index-details-content"]}>
      <IndexScoreContainer data={score} />
      <h2 className={styles["main-title"]}>
        {t("index:container.yourWoneIndex")}
      </h2>
      {score?.descriptionHtml && (
        <p
          className={styles["main-description"]}
          dangerouslySetInnerHTML={{ __html: score.descriptionHtml }}
        />
      )}
      <div className="mt-3 row">
        <div
          className={clsx(styles["chart-container"], "mt-4 col-12 col-xxl-4")}
        >
          <h3 className={styles["chart-title"]}>
            <img src={IndexScoreIcon} alt="Self-assessment score icon" />{" "}
            {score.graph.scoreTitle}
          </h3>
          <p className={styles["chart-description"]}>
            {score.graph.scoreSubtitle}
          </p>
          <IndexScoreGraph
            view="compact"
            dataPeriods={score.graph.periods}
            periodLength={periodLength}
            onChangePeriodLength={handleChangePeriodLength}
            periodIndex={periodIndex}
            onClickNextPeriod={handleClickNextPeriod}
            onClickPreviousPeriod={handleClickPreviousPeriod}
            moreLoading={shouldFetchMore && loading}
          />
        </div>
        <div
          className={clsx(styles["chart-container"], "mt-4 col-12 col-xxl-4")}
        >
          <h3 className={styles["chart-title"]}>
            <img
              src={SelfAssessmentScoreIcon}
              alt="Self-assessment score icon"
            />{" "}
            {score.graph.assessmentScoreTitle}
          </h3>
          <p className={styles["chart-description"]}>
            {score.graph.assessmentScoreSubtitle}
          </p>
          <IndexScoreGraph
            view="compact"
            dataPeriods={score.graph.periods}
            periodLength={periodLength}
            onChangePeriodLength={handleChangePeriodLength}
            periodIndex={periodIndex}
            onClickNextPeriod={handleClickNextPeriod}
            onClickPreviousPeriod={handleClickPreviousPeriod}
            displayValues="assessment"
            moreLoading={shouldFetchMore && loading}
          />
        </div>
        <div
          className={clsx(styles["chart-container"], "mt-4 col-12 col-xxl-4")}
        >
          <h3 className={styles["chart-title"]}>
            <img src={BiometricsIcon} alt="Biometrics icon" />{" "}
            {score.graph.biometricsScoreTitle}
          </h3>
          <p className={styles["chart-description"]}>
            {score.graph.biometricsScoreSubtitle}
          </p>
          <IndexScoreGraph
            view="compact"
            dataPeriods={score.graph.periods}
            periodLength={periodLength}
            onChangePeriodLength={handleChangePeriodLength}
            periodIndex={periodIndex}
            onClickNextPeriod={handleClickNextPeriod}
            onClickPreviousPeriod={handleClickPreviousPeriod}
            displayValues="biometrics"
            moreLoading={shouldFetchMore && loading}
          />
        </div>
      </div>
    </div>
  );
};

const WoneIndexDetails = () => {
  const { t } = useTranslation("index");
  const { learner } = useAuth();
  const {
    screens: {
      index: { version: screenVersion },
    },
  } = learner;
  const screenQuery =
    screenVersion === "v1" ? LEARNER_SCREEN_INDEX_V1 : LEARNER_SCREEN_INDEX_V2;
  const { loading, error, data, fetchMore } = useQuery<LearnerScreenIndexQuery>(
    screenQuery,
    {
      variables: {
        appInfo,
        first: 1,
        offset: 0,
      },
    }
  );

  if (loading && !data) {
    return (
      <PageContainer>
        <PageHeader transparent />
        <PageContent maxWidth="lg">
          <PageLoading />
        </PageContent>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader transparent />
        <PageContent maxWidth="lg">
          <PageError />
        </PageContent>
      </PageContainer>
    );
  }

  const {
    learner: {
      screens: {
        index: { showBetaFlag, score },
      },
    },
  } = data;

  return (
    <PageContainer>
      <PageHeader
        showBackButton
        backTo={"/user/woneindex"}
        title={t("index:navTitles.index")}
      />
      <PageContent maxWidth="lg">
        {showBetaFlag === true && (
          <p className={styles["beta-card"]}>{t("index:badge.beta")}</p>
        )}
        <WoneIndexDetailsContent
          score={score as LearnerIndexScore}
          fetchMore={fetchMore}
          loading={loading}
        />
      </PageContent>
    </PageContainer>
  );
};

export default WoneIndexDetails;
